import React, { useEffect, useState } from "react";
import {
	Cell,
	Label,
	Pie,
	PieChart,
	ResponsiveContainer,
	Tooltip,
} from "recharts";
import { SelectPicker } from "rsuite";
import { numberFormatter } from "../../utils";

const data = [
	{ name: "Group A", value: 400 },
	{ name: "Group B", value: 300 },
	{ name: "Group C", value: 300 },
	{ name: "Group D", value: 200 },
];

const COLORS = ["#3B60A0", "#86BC25", "#012169", "#0097A9"];

const NIALeftPanel = (props: any) => {
	const { spendBreakdownByCategories, kpi } = props?.data;
	const { targetEffectiveRebate } = props?.kpiChnagingData;
	const { selectedFilter, filterData, onFilterSelect, value } = props;
	const [pieChartData, setPieChartData] = useState({ data: [], sum: 0 });
	const spendText = props.spendText;
	const [windowSize, setWindowSize] = useState({
		width: window.innerWidth,
		height: window.innerHeight,
	});

	useEffect(() => {
		function handleResize() {
			setWindowSize({
				width: window.innerWidth,
				height: window.innerHeight,
			});
		}
		window.addEventListener("resize", handleResize);
		handleResize();
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	useEffect(() => {
		let finalData: any = [],
			sum = 0;
		if (spendBreakdownByCategories) {
			for (let key in spendBreakdownByCategories) {
				let obj = {
					name: key,
					value: spendBreakdownByCategories[key],
				};
				sum += spendBreakdownByCategories[key];
				finalData.push(obj);
			}
			setPieChartData({ data: finalData, sum: sum });
		}
	}, [spendBreakdownByCategories]);

	const CustomTooltip = ({ active, payload }: any) => {
		if (active && payload && payload.length) {
			const popupStyle = {
				width: "fit-content",
				background: "white",
				border: "1px solid #ccc",
				boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
				borderRadius: "1vh",
				color: "black",
				padding: 0,
			};

			const headerStyle = {
				fontWeight: "bold",
				textTransform: "uppercase",
				background: "#f0f4f8",
				padding: "1vh",
				borderRadius: "1vh 1vh 0 0",
				fontSize: "0.95vw",
				color: "#3f3f3f",
			};

			const rowStyle = {
				color: "#3f3f3f",
				fontWeight: "normal",
				padding: "0.5vh 1vh",
				fontSize: "1vw",
			};

			return (
				<div style={popupStyle}>
					{/* Header Row */}
					<div>{payload[0]?.payload?.cluster}</div>
					{/* Data Rows */}
					<div className="h-fit p-[1vh] mt-[.5vh] mb-[.5vh]">
						{payload.map((entry: any, index: any) => (
							<p key={index} style={rowStyle}>
								{entry?.name?.toUpperCase()} : $
								{numberFormatter(entry?.value)}
							</p>
						))}
					</div>
				</div>
			);
		}
		return null;
	};

	const RADIAN = Math.PI / 180;
	const renderCustomizedLabel = (data: any) => {
		const { cx, cy, midAngle, innerRadius, outerRadius, payload } = data;
		const radius = innerRadius + (outerRadius - innerRadius) * 1.2;
		const x = cx + radius * Math.cos(-midAngle * RADIAN);
		const y = cy + radius * Math.sin(-midAngle * RADIAN);

		return (
			<text
				x={x}
				y={y}
				fill={data?.fill}
				textAnchor={x > cx ? "start" : "end"}
				fontSize={"1dvw"}
				dominantBaseline="central"
			>
				${numberFormatter(payload?.payload?.value)}
			</text>
		);
	};

	return (
		<>
			<div className="sub-heading-text font-bold flex flex-col items-start">
				<span className="filterHead">Vendor</span>
				<SelectPicker
					className="customwhite vendor-dropdown"
					onChange={(value) => {
						onFilterSelect(value, 1);
					}}
					data={filterData?.vendors}
					labelKey="vendor_name"
					valueKey="vendor_name"
					value={selectedFilter?.vendor_name[0]}
					cleanable={false}
				/>
			</div>
			<div className="contentBox h-[50%] mt-[1.5dvh]">
				<div className="headSpace">
					<div className="headContent">
						{spendText} Breakdown by Categories
					</div>
				</div>
				<ResponsiveContainer width="100%" height="70%">
					<PieChart>
						<Pie
							data={pieChartData.data}
							cx="50%"
							cy="50%"
							innerRadius="50%"
							outerRadius="90%"
							fill="#8884d8"
							paddingAngle={0}
							dataKey="value"
							label={renderCustomizedLabel}
							labelLine={false}
							nameKey="name"
						>
							{pieChartData.data.map((entry, index) => (
								<Cell
									key={`cell-${index}`}
									fill={COLORS[index % COLORS.length]}
								/>
							))}
							<Label
								value={"$" + numberFormatter(pieChartData.sum)}
								position="center"
								fill="black"
								fontSize="2.8vh"
								className="label-top"
							/>
						</Pie>
					</PieChart>
				</ResponsiveContainer>
				<div className="contentFooter h-[3vh]">
					<div className="flex space-x-2 ml-auto pr-2 mt-[2vh]">
						{pieChartData.data.map((entry: any, index: number) => (
							<div
								key={`legend-${index}`}
								className="flex items-center"
							>
								<div
									className="w-3 h-3 mr-1"
									style={{
										backgroundColor:
											COLORS[index % COLORS.length],
									}}
								></div>
								<span
									className="text-[0.75em]"
									style={{
										color: COLORS[index % COLORS.length],
									}}
								>
									{entry.name}
								</span>
							</div>
						))}
					</div>
				</div>
			</div>
			<div className="h-[35%] contentBox w-full mt-[2.2dvh] flex justify-center">
				<div className="grid grid-cols-1  w-full ">
					<div className="headSpace">
						<div className="headContent">Key Metrics</div>
					</div>
					<div className="flex justify-between items-center">
						<span className="headContent ">
							Current Effective Rebate
						</span>
						<div className="flex gap-x-2">
							<div className="bg-white font-bold text-center text-sm text-black  py-1">
								{(kpi?.currenEffectiveRebate * 100).toFixed(2)}%
							</div>
							<div className="bg-white font-bold text-center text-sm text-black  py-1">
								{numberFormatter(
									kpi?.currenEffectiveRebateDollar,
									2,
									"$"
								)}
							</div>
						</div>
					</div>
					<div className="flex justify-between items-center">
						<span className="headContent">
							Target Effective Rebate
						</span>
						<div className="flex gap-x-2">
							<div className="bg-white font-bold text-center text-sm text-black  py-1">
								{(targetEffectiveRebate * 100).toFixed(2)}%
							</div>
							<div className="bg-white font-bold text-center text-sm text-black  py-1">
								{numberFormatter(
									kpi?.targetEffectiveRebateDollar,
									2,
									"$"
								)}
							</div>
						</div>
					</div>
					<div className="flex justify-between items-center">
						<span className="headContent">Total Benefit</span>
						<div className="bg-white font-bold text-center text-sm text-black py-1">
							${numberFormatter(kpi?.totalBenefit)}
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export default NIALeftPanel;
